"use client";

import {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

// -----------------------------------------------------------------------------
// RevealOnScroll — the site's canonical entrance animation, extracted from the
// home sections (see SolutionSection). On scroll into view the child fades +
// rises once; `index` staggers a grid into a cascade. Honors
// prefers-reduced-motion (shows immediately, no transition). Wrap any block.
// -----------------------------------------------------------------------------

export type IRevealOnScrollProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  /** Position in a sequence — used to stagger the cascade (capped per row). */
  index?: number;
  /** Per-step delay in ms. */
  step?: number;
};

const RevealOnScroll: FC<IRevealOnScrollProps> = ({
  children,
  index = 0,
  step = 70,
  className,
  ...other
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // animate once only
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={clsx(
        "transition-all duration-700 ease-out motion-reduce:transition-none",
        inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
        className,
      )}
      style={{ transitionDelay: inView ? `${(index % 6) * step}ms` : "0ms" }}
      {...other}
    >
      {children}
    </div>
  );
};

export default RevealOnScroll;
